import { LoadingOutlined } from "@ant-design/icons";
import CategoryCard from "components/card/CategoryCard";
import { debounce } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { searchTerm, setSearchTermInput } from "store/search/search.slice";
import { useEffectDeviceAndOrientation } from "utils/hooks";
import "./SearchForm.scss";
import SearchItem from "./SearchItem";

const SearchForm = () => {
  const { t } = useTranslation();
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const [device, orientation] = useEffectDeviceAndOrientation();

  const [showDropdown, setShowDropdown] = useState<boolean>(false);

  const { is_loading, is_fetch, data } = useSelector(
    (store: any) => store.search.searchForm
  );
  const { searchTermInput } = useSelector((store: any) => store.search);

  // Debounce funkcija za pretragu
  const debouncedSearch = debounce((term: string) => {
    dispatch(searchTerm(term));
    setShowDropdown(true);
  }, 300); // 300 ms pauza

  const handleClickButton = debounce((term: string) => {
    setShowDropdown(false);
    const params = new URLSearchParams();
    params.set("term", term);
    navigate({
      pathname: "/search",
      search: `?${params.toString()}`,
    });
  }, 300);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    dispatch(setSearchTermInput(newValue));

    // Pozovi debounced pretragu
    if (newValue) {
      debouncedSearch(newValue);
    } else {
      // Očisti rezultate kada je input prazan
      setShowDropdown(false);
      dispatch(searchTerm(""));
    }
  };

  const hideDropdown = (e: any) => {
    setShowDropdown(false);
    dispatch(setSearchTermInput(""));
  };

  const searchRef = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleClickOutside = (event: MouseEvent) => {
    if (
      searchRef.current &&
      !searchRef.current.contains(event.target as Node)
    ) {
      setShowDropdown(false);
      dispatch(setSearchTermInput(""));
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  const handleKeyDown = (e: any) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleClickButton(e.target.value);
    }
  };

  return (
    <div className="Search-Box">
      {/* <SearchBar onSearch={handleSearch} /> */}
      <div className="Search-Container" ref={searchRef}>
        <form className="Search-Form">
          <div className="Search-Bar">
            <input
              type="search"
              placeholder={t("pretrazite_proizvode") || ""}
              aria-label={t("pretrazite_proizvode") || ""}
              value={searchTermInput}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              className="Search-Input"
            />
            <button
              className="Search-Button"
              type="button"
              onClick={() => handleClickButton(searchTermInput)} // Pozovi pretragu kada korisnik klikne dugme
            >
              Traži
            </button>
          </div>
        </form>
        {showDropdown && (
          <div
            className="Search-Dropdown-Content"
            style={
              device === "mobile" || orientation === "portrait"
                ? { width: "100%" }
                : { width: "120%" }
            }
          >
            {is_loading ? (
              <LoadingOutlined />
            ) : (
              <div>
                <div className="Search-Dropdown-Title">
                  Pretraženi proizvodi
                </div>
                <div className="Search-Article-Dropdown-Item">
                  {!is_fetch ? (
                    <label>Nisu pronađeni proizvodi</label>
                  ) : (
                    <div className="Search-Article-Item-Box">
                      {data.articles.map((itemData: any, key: any) => (
                        <SearchItem
                          data={itemData}
                          key={key}
                          hideDropdown={hideDropdown}
                        />
                      ))}
                    </div>
                  )}
                </div>
                <div className="Search-Dropdown-Title">
                  Pretražene kategorije
                </div>
                <div className="Search-Category-Dropdown-Item">
                  {!is_fetch ? (
                    <label>Nisu pronađene kategorije</label>
                  ) : (
                    <div className="Search-Category-Item-Box">
                      {data.article_categories.map(
                        (itemData: any, key: any) => (
                          <div
                            className="Search-Category-Item-Box-Item"
                            key={key}
                          >
                            <CategoryCard
                              data={itemData}
                              key={key}
                              action={hideDropdown}
                            />
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
export default SearchForm;
